import { http } from "../../services";

const PAGE_SIZE = 50;
const POLLING_INTERVAL = 30000; // 30 seconds

let pollingTimeout: NodeJS.Timeout | null = null;
let isPolling = false;

export const onLoad: producer = async ({
  ssn = get.ssn,
  error = update.views[prop._viewId].data.error,
  currentEnvelopes = get.envelopes,
  envelopes = update.envelopes,
  jwtToken = get.jwtToken,
  isLoading = observe.isLoading,
  isLoadingMore = observe.isLoadingMore,
  updateIsLoading = update.isLoading,
  updateHasMore = update.hasMore,
  updateOffset = update.offset,
  isAuthenticatedView = observe.views[prop._viewId].data.isAuthenticated
}) => {
  if (pollingTimeout) {
    clearTimeout(pollingTimeout);
    pollingTimeout = null;
  }

  if (isLoading || isLoadingMore || !isAuthenticatedView) {
    return;
  }

  if (!ssn.value() || !jwtToken.value()) {
    console.log("Missing required data for loading envelopes");
    return;
  }

  const isInitialLoad = !currentEnvelopes.value()?.length;
  if (isInitialLoad) {
    updateIsLoading.set(true);
  }

  const fetchEnvelopes = async () => {
    if (isPolling) {
      return;
    }
    
    try {
      isPolling = true;

      const res = await http({
        method: "GET",
        url: `/api/recipient-dashboard/envelope?offset=0&pageSize=${PAGE_SIZE}&timestamp=${new Date().getTime()}`,
        headers: {
          "Authorization": `Bearer ${jwtToken.value()}`
        }
      });

      let newEnvelopes = [...(currentEnvelopes.value() || [])];
      const currentEnvelopesId = newEnvelopes.map(envelope => envelope.envelopeId);
      let hasChanges = false;

      res.data.recipients.forEach(recipient => {
        const existingIndex = currentEnvelopesId.indexOf(recipient.envelopeId);
        if (existingIndex === -1) {
          newEnvelopes = [recipient, ...newEnvelopes];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        envelopes.set(newEnvelopes);
        updateHasMore.set(res.data.count > newEnvelopes.length);
        updateOffset.set(newEnvelopes.length);
      }

    } catch (e: any) {
      console.error("Error loading envelopes:", e);
      error.set(e.message);
    } finally {
      isPolling = false;
      if (isInitialLoad) {
        setTimeout(() => {
          if(jwtToken.value()) {
            updateIsLoading.set(false);
          }
        }, 10 * 1000);
      }
    }
  };

  await fetchEnvelopes();

  if (isAuthenticatedView) {
    const setupNextPoll = () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
      
      pollingTimeout = setTimeout(async () => {
        await fetchEnvelopes();
        if (isAuthenticatedView) {
          setupNextPoll();
        }
      }, POLLING_INTERVAL);
    };

    setupNextPoll();
  }
};

export const onLoadMore: producer = async ({
  ssn = get.ssn,
  error = update.views[prop._viewId].data.error,
  currentEnvelopes = get.envelopes,
  envelopes = update.envelopes,
  jwtToken = get.jwtToken,
  shouldLoadMore = observe.shouldLoadMore,
  currentOffset = get.offset,
  updateShouldLoadMore = update.shouldLoadMore,
  updateOffset = update.offset,
  hasMore = observe.hasMore,
  updateHasMore = update.hasMore,
  updateIsLoadingMore = update.isLoadingMore,
}) => {
  if (!shouldLoadMore) {
    return;
  }
  
  updateShouldLoadMore.set(false);
  
  if (!ssn.value()) {
    return;
  }
  
  if (!hasMore) {
    return;
  }

  try {
    updateIsLoadingMore.set(true);
    
    const res = await http({
      method: "GET",
      url: `/api/recipient-dashboard/envelope?offset=${currentOffset.value()}&pageSize=${PAGE_SIZE}&timestamp=${new Date().getTime()}`,
      headers: {
        "Authorization": `Bearer ${jwtToken.value()}`
      }
    });

    let newEnvelopes = [...currentEnvelopes.value()];
    const currentEnvelopesId = newEnvelopes.map((_envelope: any) => _envelope.envelopeId);
    
    res.data.recipients.forEach(recipient => {
      if (!currentEnvelopesId.includes(recipient.envelopeId)) {
        newEnvelopes = [...newEnvelopes, recipient];
      }
    });

    envelopes.set(newEnvelopes);
    updateOffset.set(currentOffset.value() + PAGE_SIZE);
    updateHasMore.set(res.data.count > (currentOffset.value() + PAGE_SIZE));

  } catch (e: any) {
    console.error("Error loading more envelopes:", e);
    error.set(e.message);
  } finally {
    updateIsLoadingMore.set(false);
  }
};

export const cleanup = () => {
  if (pollingTimeout) {
    clearTimeout(pollingTimeout);
    pollingTimeout = null;
  }
  isPolling = false;
};